import {Skeleton} from '@mantine/core';
import {useEffect, useRef} from 'react';
import {MdArrowForward, MdArrowForwardIos} from 'react-icons/md';
import {COMPANY_FOLLOW_RANKING} from '../../../../../config/constants/routes';
import {
  FOLLOW_COMPANY_RANKING_TERMS,
  FOLLOW_COMPANY_RANKING_TERM_LABELS,
} from '../../../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../../../core/entities/company/schema';
import type {ResultGetFollowCompanyRankingList} from '../../../../../core/repositories/companyRepository';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {createPathWithQuery} from '../../../../../shared/services/createPathWithQuery';
import {CompanyCardFollowRanking} from './CompanyCardFollowRanking';
import {FollowRankingTermSelector} from './FollowRankingTermSelector';

type Props = {
  ranking?: ResultGetFollowCompanyRankingList;
  isLoadingRanking: boolean;
  rankingTerm: FollowRankingListTermType;
  setRankingTerm: React.Dispatch<
    React.SetStateAction<FollowRankingListTermType>
  >;

  industryIds?: number[];
  industryCategoryId?: number;
  isEngineerCompany?: boolean;
};

export const FollowCompanyRankingFrame: React.FC<Props> = ({
  ranking,
  isLoadingRanking,
  rankingTerm,
  setRankingTerm,
  industryIds,
  industryCategoryId,
  isEngineerCompany,
}) => {
  const href =
    industryIds && industryIds.length > 0
      ? createPathWithQuery(
          COMPANY_FOLLOW_RANKING,
          {term: rankingTerm, industry_id: industryIds},
          'none',
        )
      : industryCategoryId && industryCategoryId > 0
        ? createPathWithQuery(
            COMPANY_FOLLOW_RANKING,
            {
              term: rankingTerm,
              industry_category_id: industryCategoryId,
            },
            'none',
          )
        : isEngineerCompany
          ? createPathWithQuery(
              COMPANY_FOLLOW_RANKING,
              {term: rankingTerm, is_engineer_company: isEngineerCompany},
              'none',
            )
          : createPathWithQuery(
              COMPANY_FOLLOW_RANKING,
              {
                term: rankingTerm,
              },
              'none',
            );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [rankingTerm]);

  return (
    <div id="ranking" className="w-full odd:bg-white py-8 px-16 pc:px-0">
      <div className="mx-auto pc:w-[1200px]">
        <div className="flex justify-between">
          <CommonLink href={href} className="flex items-center">
            <h2 className="font-bold text-black text-xl">
              フォロー数ランキング
            </h2>
            <span className="p-4">
              <MdArrowForwardIos size={20} />
            </span>
          </CommonLink>
          <FollowRankingTermSelector
            rankingTerm={rankingTerm}
            setRankingTerm={setRankingTerm}
            inTitleSection={true}
          />
        </div>

        <div
          ref={scrollContainerRef}
          className="flex py-16 gap-16 pc:gap-24 overflow-x-scroll"
        >
          {ranking?.ok &&
            (ranking.value.ranking.length > 0 ? (
              ranking.value.ranking.map((item) => {
                return (
                  <CompanyCardFollowRanking
                    key={item.company.id}
                    rankingItem={item}
                    isAllTerm={rankingTerm === FOLLOW_COMPANY_RANKING_TERMS.all}
                  />
                );
              })
            ) : (
              <p>
                {FOLLOW_COMPANY_RANKING_TERM_LABELS[rankingTerm]}
                フォロー数ランキングは0件です
              </p>
            ))}

          {ranking?.ok && ranking.value.ranking.length > 0 && (
            <CommonLink
              href={href}
              className="shadow-card bg-white p-16 rounded-16 hover:opacity-70 m-4 flex items-center"
            >
              <div className="flex justify-center py-16">
                <MdArrowForward size={40} />
              </div>
              <div>
                <div className="text-md text-black text-center w-[200px]">
                  もっと見る
                </div>
              </div>
            </CommonLink>
          )}

          {isLoadingRanking &&
            Array.from({length: 10}).map((_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order is not important
              <div key={index}>
                <Skeleton height={192} width={200} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
